import axios from 'axios';
import logger from '../utils/logger';

// API URL: Use Railway production URL by default, fallback to localhost for development
// To override, set REACT_APP_API_URL environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://ai-astrologer-api-production.up.railway.app'
    : 'http://localhost:8000');

// Lazy getter for supabase to avoid circular dependency
let _supabase: any = null;
const getSupabase = () => {
  if (!_supabase) {
    const { supabase } = require('./supabase');
    _supabase = supabase;
  }
  return _supabase;
};

// Cache for session to avoid repeated slow calls
let sessionCache: { session: any; timestamp: number } | null = null;
let sessionPromiseInFlight: Promise<any> | null = null;
const SESSION_CACHE_MS = 1000; // Cache for 1 second

const getSessionWithCache = async (supabase: any) => {
  const now = Date.now();

  // Return cached session if fresh
  if (sessionCache && (now - sessionCache.timestamp) < SESSION_CACHE_MS) {
    return sessionCache.session;
  }

  // If a request is already in flight, wait for it instead of making a new one
  if (sessionPromiseInFlight) {
    return sessionPromiseInFlight;
  }

  // Get fresh session with timeout
  const fetchSession = async () => {
    try {
      const sessionPromise = supabase.auth.getSession();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('getSession timeout')), 5000)
      );

      const result = await Promise.race([sessionPromise, timeoutPromise]);
      sessionCache = { session: result, timestamp: Date.now() };
      return result;
    } catch (error: any) {
      // If timeout and we have a cached session, use it even if stale
      if (error.message === 'getSession timeout' && sessionCache) {
        return sessionCache.session;
      }
      throw error;
    } finally {
      sessionPromiseInFlight = null;
    }
  };

  sessionPromiseInFlight = fetchSession();
  return sessionPromiseInFlight;
};

// Public endpoints that don't require authentication
const PUBLIC_ENDPOINTS = [
  '/health',
  '/docs',
  '/openapi.json'
];

// Helper function to check if endpoint is public
const isPublicEndpoint = (url: string | undefined): boolean => {
  if (!url) return false;
  // Remove base URL and query params for comparison
  const path = url.replace(API_BASE_URL, '').split('?')[0];
  // Exact match only - don't use startsWith to avoid false positives
  return PUBLIC_ENDPOINTS.includes(path);
};

// Create axios instance with default config
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 120 second timeout for chart calculation (multiple AI calls can take time)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add Supabase auth token
api.interceptors.request.use(
  async (config) => {
    logger.debug('API', 'Request interceptor', { method: config.method, url: config.url });

    // Skip auth for public endpoints
    if (isPublicEndpoint(config.url)) {
      logger.debug('API', 'Skipping auth for public endpoint', { url: config.url });
      return config;
    }

    try {
      const supabase = getSupabase();
      const { data: { session }, error } = await getSessionWithCache(supabase);

      if (error) {
        logger.error('API', 'Session retrieval error', error, { url: config.url });
        return Promise.reject(new Error('Authentication session invalid'));
      }

      if (session?.access_token) {
        logger.debug('API', 'Adding auth token to request', { url: config.url });
        config.headers.Authorization = `Bearer ${session.access_token}`;
        return config;
      } else {
        logger.warn('API', 'No session found for protected endpoint', { url: config.url });
        return Promise.reject(new Error('No valid authentication session'));
      }
    } catch (err: any) {
      logger.error('API', 'Request interceptor exception', err);
      return Promise.reject(err);
    }
  },
  (error) => {
    logger.error('API', 'Request interceptor error', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    logger.debug('API', 'Response received', {
      method: response.config.method,
      url: response.config.url,
      status: response.status
    });
    return response;
  },
  async (error) => {
    logger.apiError('API', error.config?.method?.toUpperCase() || 'UNKNOWN', error.config?.url || 'unknown', error);

    // Handle email account conflict (409) - must be before general auth errors
    if (error.response?.status === 409) {
      const errorCode = error.response?.data?.detail?.error_code ||
        error.response?.data?.error_code;

      if (errorCode === 'EMAIL_ACCOUNT_CONFLICT') {
        logger.warn('API', 'Email account conflict detected - forcing logout', {
          url: error.config?.url,
          message: error.response?.data?.detail?.message || error.response?.data?.message
        });

        // Sign out from Supabase
        const supabase = getSupabase();
        await supabase.auth.signOut();

        // Clear all local storage
        localStorage.clear();

        // Redirect to landing page
        window.location.href = '/';
        return; // Don't reject promise, just redirect
      }
    }

    // Handle authentication/authorization errors
    if (error.response?.status === 401 || error.response?.status === 403) {
      logger.warn('API', `Authentication error (${error.response.status})`, {
        status: error.response.status,
        url: error.config?.url,
        detail: error.response?.data?.detail
      });

      // In development, don't auto-logout on errors - backend might be reloading
      // This prevents annoying logouts during development
      const isDev = process.env.NODE_ENV === 'development';

      if (!isDev) {
        logger.warn('API', 'Signing out user due to auth error');
        const supabase = getSupabase();
        await supabase.auth.signOut();
        localStorage.removeItem('user');
        window.location.href = '/';
        return;
      }

      // In dev mode, just log the error and let the app handle it
      logger.debug('API', 'Skipping auto-logout in development mode');
    }

    return Promise.reject(error);
  }
);

// API endpoints
export const healthCheck = async () => {
  logger.apiCall('API', 'GET', '/health');
  const response = await api.get('/health');
  logger.apiResponse('API', 'GET', '/health', response.status);
  return response.data;
};

// Auth endpoints (to be implemented in Phase 1)
// export const loginWithGoogle = async (googleToken: string) => { ... }
// export const refreshToken = async () => { ... }

// User endpoints
export const getUserProfile = async () => {
  logger.apiCall('API', 'GET', '/user/profile');
  const response = await api.get('/user/profile');
  logger.apiResponse('API', 'GET', '/user/profile', response.status);
  return response;
};

// Cached version of getUserProfile with 30 minute TTL
const USER_PROFILE_TTL_MS = 30 * 60 * 1000; // 30 minutes
export const getUserProfileCached = async () => {
  const key = 'GET:/user/profile';
  const cached = getCache<any>(key, USER_PROFILE_TTL_MS);
  if (cached) {
    logger.debug('API', 'Serving cached user profile');
    return cached;
  }
  logger.apiCall('API', 'GET', '/user/profile');
  const response = await api.get('/user/profile');
  logger.apiResponse('API', 'GET', '/user/profile', response.status);
  setCache(key, response);
  return response;
};

export const updateUserProfile = async (data: { name?: string; gender?: string }) => {
  logger.apiCall('API', 'PUT', '/user/profile', data);
  const response = await api.put('/user/profile', data);
  logger.apiResponse('API', 'PUT', '/user/profile', response.status);
  return response;
};

export const updateUserPreferences = async (data: { astrology_style?: string }) => {
  logger.apiCall('API', 'PUT', '/user/preferences', data);
  const response = await api.put('/user/preferences', data);
  logger.apiResponse('API', 'PUT', '/user/preferences', response.status);
  return response;
};

export default api;

// Profile metrics and planetary influences
export const getProfileMetrics = async () => {
  const path = '/profile/metrics';
  logger.apiCall('API', 'GET', path);
  const response = await api.get(path);
  logger.apiResponse('API', 'GET', path, response.status);
  return response.data as { style: 'western' | 'vedic'; metrics: Array<{ key: string; title: string; preview: string; related_planets: string[]; related_houses: number[] }> };
};

export const getPlanetInfluences = async () => {
  const path = '/profile/planet-influences';
  logger.apiCall('API', 'GET', path);
  const response = await api.get(path);
  logger.apiResponse('API', 'GET', path, response.status);
  return response.data as { style: 'western' | 'vedic'; influences: Array<{ planet: string; summary: string; house?: number; sign?: string; retrograde?: boolean; impacts: Array<{ metric: string; note: string }> }> };
};

export const getReadingHistory = async (params?: { page?: number; limit?: number; type?: 'daily' | 'custom' }) => {
  const q = new URLSearchParams();
  if (params?.page) q.set('page', String(params.page));
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.type) q.set('type', params.type);
  const path = `/readings/history${q.toString() ? `?${q.toString()}` : ''}`;
  logger.apiCall('API', 'GET', path);
  const response = await api.get(path);
  logger.apiResponse('API', 'GET', path, response.status);
  return response.data as { page: number; limit: number; total: number; results: Array<{ id: string; session_type: string; initial_question: string; created_at: string; last_activity_at: string; preview?: string }> };
};

// Structured personality and planet blurbs
export const getPersonalityCards = async () => {
  const path = '/profile/personality-cards';
  logger.apiCall('API', 'GET', path);
  const response = await api.get(path);
  logger.apiResponse('API', 'GET', path, response.status);
  return response.data as { cards: Array<{ title: string; body: string; tag: string }> };
};

export const getPlanetBlurbs = async () => {
  const path = '/profile/planet-blurbs';
  logger.apiCall('API', 'GET', path);
  const response = await api.get(path);
  logger.apiResponse('API', 'GET', path, response.status);
  return response.data as { blurbs: Array<{ planet: string; text: string }> };
};

export const getPersonalityQuote = async () => {
  const path = '/profile/quote';
  logger.apiCall('API', 'GET', path);
  const response = await api.get(path);
  logger.apiResponse('API', 'GET', path, response.status);
  return response.data as { quote: string };
};

// Cached version of getPersonalityQuote with 30 minute TTL
const PERSONALITY_QUOTE_TTL_MS = 30 * 60 * 1000; // 30 minutes
export const getPersonalityQuoteCached = async () => {
  const key = 'GET:/profile/quote';
  const cached = getCache<{ quote: string }>(key, PERSONALITY_QUOTE_TTL_MS);
  if (cached) {
    logger.debug('API', 'Serving cached personality quote');
    return cached;
  }
  const path = '/profile/quote';
  logger.apiCall('API', 'GET', path);
  const response = await api.get(path);
  logger.apiResponse('API', 'GET', path, response.status);
  const data = response.data as { quote: string };
  setCache(key, data);
  return data;
};

// Prefetch function to load user data and quote in the background
// This should be called when the app loads and user is authenticated
export const prefetchUserData = async () => {
  logger.debug('API', 'Prefetching user data and quote');
  try {
    // Fire both requests in parallel without blocking
    await Promise.all([
      getUserProfileCached().catch((err: any) => {
        logger.warn('API', 'Prefetch user profile failed', { error: err?.message || String(err) });
      }),
      getPersonalityQuoteCached().catch((err: any) => {
        logger.warn('API', 'Prefetch personality quote failed', { error: err?.message || String(err) });
      })
    ]);
    logger.debug('API', 'Prefetch complete');
  } catch (err: any) {
    logger.warn('API', 'Prefetch error', { error: err?.message || String(err) });
  }
};

// Profile content generation
export const generateProfileContent = async () => {
  const path = '/profile/generate';
  logger.apiCall('API', 'POST', path);
  const response = await api.post(path, {});
  logger.apiResponse('API', 'POST', path, response.status);
  return response.data as { style: 'western' | 'vedic'; cards_count: number; blurbs_count: number };
};

export const regenerateProfileContent = async (scope: 'all' | 'cards' | 'planets' = 'all') => {
  const q = new URLSearchParams();
  q.set('scope', scope);
  const path = `/profile/regenerate?${q.toString()}`;
  logger.apiCall('API', 'PATCH', path);
  const response = await api.patch(path, {});
  logger.apiResponse('API', 'PATCH', path, response.status);
  return response.data as { style: 'western' | 'vedic'; scope: string; cards_count: number; blurbs_count: number };
};

// Reading endpoints
export const postDailyReading = async (day: 'today' | 'tomorrow' = 'today') => {
  logger.apiCall('API', 'POST', '/reading/daily', { day });
  const response = await api.post('/reading/daily', { day });
  logger.apiResponse('API', 'POST', '/reading/daily', response.status);
  return response.data as { content: string; created_at: string; session_id?: string; day?: string };
};

type StreamParams =
  | { type: 'daily'; day: 'today' | 'tomorrow'; mode?: 'western' | 'vedic' }
  | { type: 'custom'; question: string; session_id?: string; mode?: 'western' | 'vedic' };

// Track active EventSource connections for debugging
let activeConnections = 0;
const connectionIds = new Map<EventSource, string>();

export const openReadingStream = async (params: StreamParams): Promise<EventSource> => {
  const supabase = getSupabase();
  const { data: { session } } = await getSessionWithCache(supabase);
  const token = session?.access_token;
  if (!token) {
    throw new Error('No valid authentication session');
  }
  const q = new URLSearchParams();
  q.set('token', token);
  if (params.type === 'daily') {
    q.set('type', 'daily');
    q.set('day', params.day);
  } else {
    q.set('type', 'custom');
    q.set('question', params.question);
    if (params.session_id) q.set('session_id', params.session_id);
  }
  // Feature flag: pick endpoint by mode or localStorage fallback
  const mode = params.mode || (localStorage.getItem('astrology_mode') as 'western' | 'vedic' | null) || 'western';
  const path = mode === 'vedic' ? '/reading/vedic/stream' : '/reading/stream';
  const url = `${API_BASE_URL}${path}?${q.toString()}`;

  activeConnections++;
  const connectionId = `conn-${Date.now()}-${Math.random().toString(36).substring(7)}`;

  logger.apiCall('API', 'GET(SSE)', path, {
    ...params,
    connectionId,
    activeConnections,
    mode
  });

  const es = new EventSource(url);
  connectionIds.set(es, connectionId);

  // Add listeners to track connection lifecycle
  const originalClose = es.close.bind(es);
  es.close = () => {
    activeConnections = Math.max(0, activeConnections - 1);
    const id = connectionIds.get(es);
    logger.info('API', 'EventSource closed', { connectionId: id, remainingConnections: activeConnections });
    connectionIds.delete(es);
    originalClose();
  };

  es.addEventListener('error', () => {
    const id = connectionIds.get(es);
    logger.warn('API', 'EventSource error - connection may be closing', { connectionId: id, activeConnections });
  });

  es.addEventListener('open', () => {
    const id = connectionIds.get(es);
    logger.info('API', 'EventSource connection established', { connectionId: id, activeConnections });
  });

  logger.info('API', 'EventSource created', { connectionId, activeConnections, url: path });

  return es;
};

// Explicit Vedic stream helper
export const openVedicReadingStream = async (params: Omit<StreamParams, 'mode'>): Promise<EventSource> => {
  return openReadingStream({ ...params, mode: 'vedic' } as StreamParams);
};

// Release any stuck stream locks before starting a new stream
export const releaseStreamLock = async (): Promise<void> => {
  try {
    const supabase = getSupabase();
    const { data: { session } } = await getSessionWithCache(supabase);
    const token = session?.access_token;
    if (!token) return;

    await api.delete(`/reading/stream/release-lock?token=${encodeURIComponent(token)}`);
    logger.debug('API', 'Released stream locks');
  } catch (err) {
    // Ignore errors - lock may not exist or endpoint may fail
    logger.debug('API', 'Failed to release stream lock (may not exist)', { error: String(err) });
  }
};

// Follow-up flow
export const startFollowup = async (question: string, type: 'custom' | 'daily' = 'custom') => {
  logger.apiCall('API', 'POST', '/reading/followup', { question, type });
  const response = await api.post('/reading/followup', { question, type });
  logger.apiResponse('API', 'POST', '/reading/followup', response.status);
  return response.data as { action: 'ask' | 'proceed'; session_id: string; followup?: string; language: string; skip_label: string };
};

export const replyFollowup = async (session_id: string, answer?: string, skip?: boolean) => {
  const payload: any = { session_id };
  if (answer && answer.trim()) payload.answer = answer.trim();
  if (skip) payload.skip = true;
  logger.apiCall('API', 'POST', '/reading/followup', payload);
  const response = await api.post('/reading/followup', payload);
  logger.apiResponse('API', 'POST', '/reading/followup', response.status);
  return response.data as { action: 'ask' | 'proceed'; session_id: string; followup?: string; language: string; skip_label: string };
};

// ---------------------------------------------------------------------------
// Lightweight in-memory caching for stable resources
// ---------------------------------------------------------------------------
type CacheEntry<T> = { value: T; timestamp: number };
const apiCache = new Map<string, CacheEntry<any>>();

const getCache = <T,>(key: string, ttlMs: number): T | null => {
  const entry = apiCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > ttlMs) {
    apiCache.delete(key);
    return null;
  }
  return entry.value as T;
};

const setCache = <T,>(key: string, value: T) => {
  apiCache.set(key, { value, timestamp: Date.now() });
};

export const clearApiCache = () => {
  apiCache.clear();
};

// Cached helpers
const BACKEND_SESSION_TTL_MS = 5 * 60 * 1000; // 5 minutes
const PERSONALITY_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

export const getBackendSessionCached = async () => {
  const key = 'GET:/auth/session';
  const cached = getCache<any>(key, BACKEND_SESSION_TTL_MS);
  if (cached) {
    logger.debug('API', 'Serving cached /auth/session');
    return cached;
  }
  logger.apiCall('API', 'GET', '/auth/session');
  const response = await api.get('/auth/session');
  logger.apiResponse('API', 'GET', '/auth/session', response.status);
  setCache(key, response);
  return response;
};

export const getPersonalityCached = async (backendUserId: string) => {
  const key = `GET:/chart/personality/${backendUserId}`;
  const cached = getCache<any>(key, PERSONALITY_TTL_MS);
  if (cached) {
    logger.debug('API', `Serving cached personality for ${backendUserId}`);
    return cached;
  }
  const path = `/chart/personality/${backendUserId}`;
  logger.apiCall('API', 'GET', path);
  const response = await api.get(path);
  logger.apiResponse('API', 'GET', path, response.status);
  setCache(key, response);
  return response;
};

// Session endpoints
export const getSession = async (sessionId: string) => {
  const path = `/session/${sessionId}`;
  logger.apiCall('API', 'GET', path);
  const response = await api.get(path);
  logger.apiResponse('API', 'GET', path, response.status);
  return response.data as {
    id: string;
    session_type: string;
    initial_question: string;
    status: string;
    message_count: number;
    max_messages: number;
    created_at: string;
    last_activity_at: string;
    messages: Array<{
      id: string;
      agent_name: string;
      agent_type: string;
      content: string;
      message_kind: string;
      created_at: string;
    }>;
  };
};

